import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { webba } from '../lib/webba.js'
import { refreshSession, type StarkSession, type StarkSessionUser } from '../lib/webba-bridge.js'
import { onUnauthorized, setTokenProvider } from '../lib/api.js'

const STORAGE_KEY = 'stark_session'
const REFRESH_LOCK_KEY = 'stark_refresh_lock'
const REFRESH_BUFFER_MS = 5 * 60 * 1000
const MAX_REFRESH_RETRIES = 2
const RETRY_DELAY_MS = 1000
const LOCK_TIMEOUT_MS = 10_000

interface AuthContextValue {
  user: StarkSessionUser | null
  session: StarkSession | null
  isLoading: boolean
  login: () => Promise<void>
  logout: () => Promise<void>
  /** Persist a freshly-minted session (called from AuthCallbackPage). */
  setSession: (s: StarkSession) => void
  /** Returns a non-expired Stark JWT, refreshing via /webba/refresh if needed. */
  getValidAccessToken: () => Promise<string | null>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function loadSession(): StarkSession | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as StarkSession
    if (
      typeof parsed?.sessionJwt === 'string' &&
      typeof parsed.webbaRefreshToken === 'string' &&
      typeof parsed.sessionExpiresAt === 'number' &&
      parsed.user?.id
    ) {
      return parsed
    }
  } catch {
    // fall through
  }
  return null
}

function saveSession(s: StarkSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
}

function clearStoredSession(): void {
  localStorage.removeItem(STORAGE_KEY)
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSessionState] = useState<StarkSession | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRefreshingRef = useRef(false)

  const clearAuth = useCallback(() => {
    clearStoredSession()
    localStorage.removeItem(REFRESH_LOCK_KEY)
    setSessionState(null)
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
  }, [])

  const acquireLock = useCallback((): boolean => {
    const now = Date.now()
    const existing = localStorage.getItem(REFRESH_LOCK_KEY)
    if (existing) {
      const t = Number.parseInt(existing, 10)
      if (now - t < LOCK_TIMEOUT_MS) return false
    }
    localStorage.setItem(REFRESH_LOCK_KEY, now.toString())
    return true
  }, [])

  const releaseLock = useCallback(() => {
    localStorage.removeItem(REFRESH_LOCK_KEY)
  }, [])

  const refreshWithRetry = useCallback(
    async (refreshToken: string): Promise<StarkSession | null> => {
      for (let attempt = 0; attempt < MAX_REFRESH_RETRIES; attempt++) {
        try {
          return await refreshSession(refreshToken)
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e)
          // Permanent failures: token revoked, reused, or expired past the refresh window.
          if (msg.includes('refresh_failed') || msg.includes('invalid_grant')) return null
          if (attempt < MAX_REFRESH_RETRIES - 1) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * Math.pow(2, attempt)))
          }
        }
      }
      return null
    },
    [],
  )

  const scheduleRefresh = useCallback(
    (current: StarkSession) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
      const wait = Math.max(current.sessionExpiresAt - Date.now() - REFRESH_BUFFER_MS, 1000)

      refreshTimerRef.current = setTimeout(async () => {
        if (isRefreshingRef.current) return

        if (!acquireLock()) {
          await new Promise((r) => setTimeout(r, 2000))
          const refreshed = loadSession()
          if (refreshed && refreshed.sessionJwt !== current.sessionJwt) {
            setSessionState(refreshed)
            scheduleRefresh(refreshed)
            return
          }
          if (!acquireLock()) return
        }

        isRefreshingRef.current = true
        try {
          const stored = loadSession()
          if (!stored) return
          const fresh = await refreshWithRetry(stored.webbaRefreshToken)
          if (fresh) {
            saveSession(fresh)
            setSessionState(fresh)
            scheduleRefresh(fresh)
          } else {
            clearAuth()
          }
        } finally {
          isRefreshingRef.current = false
          releaseLock()
        }
      }, wait)
    },
    [acquireLock, clearAuth, refreshWithRetry, releaseLock],
  )

  const checkAndRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return
    const stored = loadSession()
    if (!stored) return

    const timeLeft = stored.sessionExpiresAt - Date.now()
    if (timeLeft > REFRESH_BUFFER_MS) {
      scheduleRefresh(stored)
      return
    }

    if (!acquireLock()) {
      await new Promise((r) => setTimeout(r, 2000))
      const newStored = loadSession()
      if (newStored && newStored.sessionJwt !== stored.sessionJwt) {
        setSessionState(newStored)
        scheduleRefresh(newStored)
      }
      return
    }

    isRefreshingRef.current = true
    try {
      const current = loadSession()
      if (!current) return
      const fresh = await refreshWithRetry(current.webbaRefreshToken)
      if (fresh) {
        saveSession(fresh)
        setSessionState(fresh)
        scheduleRefresh(fresh)
      } else {
        clearAuth()
      }
    } finally {
      isRefreshingRef.current = false
      releaseLock()
    }
  }, [acquireLock, clearAuth, refreshWithRetry, releaseLock, scheduleRefresh])

  const getValidAccessToken = useCallback(async (): Promise<string | null> => {
    const current = loadSession()
    if (!current) return null

    const timeLeft = current.sessionExpiresAt - Date.now()
    if (timeLeft > REFRESH_BUFFER_MS) return current.sessionJwt

    if (isRefreshingRef.current) {
      await new Promise((r) => setTimeout(r, 1000))
      return loadSession()?.sessionJwt ?? null
    }

    isRefreshingRef.current = true
    try {
      const fresh = await refreshWithRetry(current.webbaRefreshToken)
      if (fresh) {
        saveSession(fresh)
        setSessionState(fresh)
        scheduleRefresh(fresh)
        return fresh.sessionJwt
      }
      clearAuth()
      return null
    } finally {
      isRefreshingRef.current = false
    }
  }, [clearAuth, refreshWithRetry, scheduleRefresh])

  useEffect(() => {
    const init = async () => {
      const stored = loadSession()
      if (stored) {
        const timeLeft = stored.sessionExpiresAt - Date.now()
        if (timeLeft <= 0) {
          const fresh = await refreshWithRetry(stored.webbaRefreshToken)
          if (fresh) {
            saveSession(fresh)
            setSessionState(fresh)
            scheduleRefresh(fresh)
          } else {
            clearStoredSession()
          }
        } else {
          setSessionState(stored)
          scheduleRefresh(stored)
        }
      }
      setIsLoading(false)
    }
    void init()

    let debounce: ReturnType<typeof setTimeout> | null = null
    const debounced = () => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => {
        void checkAndRefresh()
      }, 500)
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') debounced()
    }
    const onFocus = () => debounced()
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return
      if (e.newValue) {
        try {
          const next = JSON.parse(e.newValue) as StarkSession
          setSessionState(next)
          scheduleRefresh(next)
        } catch {
          // ignore malformed cross-tab payload
        }
      } else {
        clearAuth()
      }
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', onFocus)
    window.addEventListener('storage', onStorage)

    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
      if (debounce) clearTimeout(debounce)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', onFocus)
      window.removeEventListener('storage', onStorage)
    }
  }, [refreshWithRetry, scheduleRefresh, checkAndRefresh, clearAuth])

  const login = useCallback(async () => {
    await webba.login()
  }, [])

  const logout = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
      refreshTimerRef.current = null
    }
    if (session) {
      try {
        await webba.logout(undefined, session.webbaRefreshToken)
      } catch {
        // ignore network errors during logout — local state is cleared anyway
      }
    }
    clearAuth()
  }, [clearAuth, session])

  const setSession = useCallback(
    (newSession: StarkSession) => {
      saveSession(newSession)
      setSessionState(newSession)
      scheduleRefresh(newSession)
    },
    [scheduleRefresh],
  )

  useEffect(() => {
    setTokenProvider(getValidAccessToken)
    const off = onUnauthorized(() => {
      clearAuth()
    })
    return () => {
      setTokenProvider(null)
      off()
    }
  }, [getValidAccessToken, clearAuth])

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      session,
      isLoading,
      login,
      logout,
      setSession,
      getValidAccessToken,
    }),
    [session, isLoading, login, logout, setSession, getValidAccessToken],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
